<template>
  <div class="bsp-card">
    <div class="bsp-card__head">
      <span class="bsp-card__icon" aria-hidden="true">
        <v-icon size="16" color="#EA580C">mdi-fire</v-icon>
      </span>
      <div class="bsp-card__title">{{ $t("bestMostProfitableProducts") }}</div>
    </div>
    <div v-if="canSee" class="bsp-card__list">
      <button
        v-for="(item, index) in items"
        :key="(item.id || item.name) + '-' + index"
        type="button"
        class="bsp-card__row"
        @click="edit(item)"
      >
        <span class="bsp-card__thumb">
          <img :src="item.avatar || placeholder" alt="" />
        </span>
        <span class="bsp-card__meta">
          <span class="bsp-card__name">{{ item.name }}</span>
          <span v-if="item.subtitle" class="bsp-card__sub">{{ item.subtitle }}</span>
        </span>
        <span class="bsp-card__amount">
          {{ $numberFormat(item.amount) }}
          {{ $store.state.currency.name }}
        </span>
      </button>
      <div v-if="!loading && !items.length" class="bsp-card__empty">
        <home-empty-state :size="80" />
      </div>
      <div v-if="loading" class="bsp-card__more">
        <v-progress-circular
          :width="3"
          color="blue"
          indeterminate
          size="22"
        />
      </div>
    </div>
    <div v-else class="bsp-card__empty">
      {{ $t("you don't have access to this information") }}
    </div>
    <button
      v-if="canSee"
      type="button"
      class="bsp-card__all"
      @click="goAll"
    >
      {{ $t("viewAll") }}
    </button>
  </div>
</template>

<script>
import HomeEmptyState from "./HomeEmptyState.vue";

export default {
  name: "HomeBestSellingProducts",
  components: { HomeEmptyState },
  props: {
    dateType: {
      type: String,
      default: "7days",
    },
  },
  data() {
    return {
      items: [],
      loading: false,
      placeholder: require("@/assets/imagePlacholder.png"),
    };
  },
  computed: {
    canSee() {
      return (
        this.$store.getters.getpermission &&
        this.$store.getters.getpermission.includes("overview")
      );
    },
  },
  watch: {
    dateType() {
      this.fetchList();
    },
  },
  mounted() {
    this.fetchList();
  },
  methods: {
    productName(item) {
      return item.product_name || item.name || item.title || "—";
    },
    productAmount(item) {
      return parseFloat(item.current || item.profit || item.amount || 0) || 0;
    },
    productSubtitle(item) {
      if (item.invoices != null) return item.invoices + " " + this.$t("sell");
      return "";
    },
    productAvatar(item) {
      const avatar = item.avatar;
      if (!avatar) return "";
      if (typeof avatar === "string") return avatar;
      return avatar.link || avatar.url || "";
    },
    goAll() {
      this.$router.push({ name: "bIsell" }).catch(() => {});
    },
    edit(item) {
      if (!item.id) return;
      this.$router
        .push({ name: "ProductEdit", params: { id: item.id } })
        .catch(() => {});
    },
    fetchList() {
      this.loading = true;
      this.$axios
        .get(this.$endpoint + "dashboard/charts/top-product-most-sell", {
          params: {
            dateType: this.dateType,
          },
        })
        .then((response) => {
          const rows =
            (response.data &&
              response.data.data &&
              response.data.data.topQuantityProducts) ||
            [];
          this.items = rows
            .map((item) => ({
              id: item.product_id || item.id,
              name: this.productName(item),
              amount: this.productAmount(item),
              avatar: this.productAvatar(item),
              subtitle: this.productSubtitle(item),
            }))
            .slice(0, 10);
        })
        .catch(() => {
          this.items = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.bsp-card {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 4px;
  padding: 12px 14px;
  height: 360px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.bsp-card__head {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  flex-shrink: 0;
}
.bsp-card__icon {
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.bsp-card__title {
  font-size: 13px;
  font-weight: 700;
  color: #111;
  line-height: 1;
}
.bsp-card__all {
  flex-shrink: 0;
  width: 100%;
  height: 34px;
  margin-top: 8px;
  border: none;
  background: none;
  color: #0066ff;
  font-size: 12px;
  cursor: pointer;
  padding: 0;
}
.bsp-card__list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
.bsp-card__list::-webkit-scrollbar {
  width: 4px;
}
.bsp-card__list::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 4px;
}
.bsp-card__row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border: none;
  border-bottom: 1px solid #ffedd5;
  background: none;
  cursor: pointer;
  text-align: start;
}
.bsp-card__row:last-child {
  border-bottom: none;
}
.bsp-card__thumb {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
  background: #ffedd5;
}
.bsp-card__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.bsp-card__meta {
  flex: 1 1 auto;
  min-width: 0;
  max-width: calc(100% - 92px);
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-inline-end: 16px;
}
.bsp-card__name {
  font-size: 12px;
  color: #111;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.bsp-card__sub {
  font-size: 11px;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.bsp-card__amount {
  flex-shrink: 0;
  margin-inline-start: auto;
  padding-inline-start: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #111;
  white-space: nowrap;
}
.bsp-card__empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #6b7280;
  text-align: center;
  min-height: 120px;
}
.bsp-card__more {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
}
</style>
